import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TIER_COPY } from "@/lib/hooks";
import { PageShell } from "@/components/AppShell";
import { Loader } from "@/components/ProtectedRoute";
import { Progress } from "@/components/ui/progress";
import { t, colon, getLang } from "@/lib/i18n";
import { MachineTranslationBanner } from "@/components/LanguagePicker";

const tx = (key, en) => (t(key) === key ? en : t(key));

const resultSummary = (r, isDiag) => {
  const raw = r.raw_score ?? r.score;
  if (getLang() === "fr") {
    return `${r.correct} bonne(s) réponse(s) sur ${r.scored} · précision brute ${raw} %${isDiag ? "" : ` · score avec aide ${r.score} % (questions avec indice : ${r.hints_used || 0})`}`;
  }
  return `${r.correct} of ${r.scored} correct · raw accuracy ${raw}%${isDiag ? "" : ` · supported score ${r.score}% (hints used on ${r.hints_used || 0})`}`;
};

const MODE_LABEL = { practice: "Practice", certified: "Certified", diagnostic: "Diagnostic" };

const Feedback = ({ fb, mode }) => {
  if (mode !== "diagnostic" && fb.rationale) {
    return (
      <div data-testid="quiz-feedback" className={`fade-up mt-6 rounded-r-lg border-l-4 bg-muted/60 p-5 ${fb.correct ? "border-brand-accent" : "border-destructive"}`}>
        <div className="flex items-center gap-2 font-heading text-lg font-bold">
          {fb.correct ? <CheckCircle2 className="h-5 w-5 text-brand-accent" /> : <XCircle className="h-5 w-5 text-destructive" />}
          {fb.correct ? t("Correct") : `${t("Not quite. The best answer is")} ${fb.correct_option}`}
        </div>
        <p className="mt-2 text-base leading-relaxed">{fb.rationale}</p>
        <p className="mt-3 text-xs text-muted-foreground">{t("Lesson")}{colon()} {fb.lesson_tag}</p>
        {mode === "practice" && <FeedbackLinks links={fb.links} />}
      </div>
    );
  }
  return (
    <div data-testid="quiz-feedback" className="mt-6 flex gap-3 border-l-4 border-brand-data bg-card p-4 text-sm">
      <Info className="h-4 w-4 shrink-0 text-brand-data" />
      <div>{t("Answer recorded.")}{fb.recommendation && <span className="block text-muted-foreground">{fb.recommendation} after this attempt.</span>}</div>
    </div>
  );
};

const FeedbackLinks = ({ links }) => {
  if (!links) return null;
  return (
    <div data-testid="quiz-feedback-links" className="mt-3 flex flex-wrap gap-2 text-xs">
      {links.lesson_id && <Link to={`/lessons/${links.lesson_id}${links.anchor ? `#${links.anchor}` : ""}`} className="border border-border px-2 py-1 font-semibold hover:bg-muted">{t("Review this lesson →")}</Link>}
    </div>
  );
};

const HintPanel = ({ attemptId, mode, hint, setHint, locked }) => {
  if (mode === "diagnostic") return null;
  const ask = async () => {
    try {
      const { data } = await api.post(`/quiz/attempts/${attemptId}/hint`);
      setHint(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="mt-6">
      {hint?.hints.map((h, i) => <p key={i} data-testid={`quiz-hint-${i + 1}`} className="mt-2 border-l-2 border-brand-primary pl-3 text-sm"><strong>{t("Hint")} {i + 1}{colon()}</strong> {h}</p>)}
      {hint?.lesson_id && <Link to={`/lessons/${hint.lesson_id}`} target="_blank" className="mt-2 inline-block text-xs text-brand-primary underline">{t("Hint lesson")}{colon()} {hint.lesson_ref}</Link>}
      {!locked && (hint?.level || 0) < 2 && (
        <button type="button" data-testid="quiz-hint-button" onClick={ask} className="mt-3 block text-sm font-semibold text-brand-primary">
          {t("Show hint")} {(hint?.level || 0) + 1}{mode === "certified" && <span className="font-normal text-muted-foreground"> — {t("a correct answer then earns")} {hint?.level ? "0.70" : "0.85"} {t("marks")}</span>}
        </button>
      )}
    </div>
  );
};

const Option = ({ o, selected, locked, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    disabled={locked}
    onClick={() => onSelect(o.key)}
    data-testid={`quiz-option-${o.key.toLowerCase()}`}
    className={`flex w-full items-start gap-4 rounded-xl border-2 p-4 text-left text-lg transition-colors sm:p-5 ${selected ? "border-brand-primary bg-brand-primary/5" : "border-border bg-card hover:border-brand-primary/60"} disabled:cursor-default`}
  >
    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 font-mono text-sm ${selected ? "border-brand-primary bg-brand-primary text-brand-primary-fg" : "border-border"}`}>{o.key}</span>
    <span className="pt-0.5">{o.text}</span>
  </button>
);

const Review = ({ items }) => (
  <section data-testid="quiz-review" className="mt-12">
    <h2 className="font-heading text-2xl font-bold">{t("Review your answers")}</h2>
    <ol className="mt-5 space-y-4">
      {items.map((q, i) => (
        <li key={q.id} data-testid={`quiz-review-${i}`} className={`rounded-r-xl border-l-4 bg-card p-5 ${q.correct ? "border-brand-accent" : "border-destructive"}`}>
          <div className="flex gap-2 font-semibold">{q.correct ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-accent" /> : <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />}<span>{i + 1}. {q.stem}</span></div>
          <p className="mt-3 text-sm">{t("Correct answer")}{colon()} <strong>{q.correct_option}. {q.options.find((o) => o.key === q.correct_option)?.text}</strong></p>
          {!q.correct && <p className="mt-1 text-sm text-muted-foreground">{t("Your answer")}{colon()} {q.selected}. {q.options.find((o) => o.key === q.selected)?.text}</p>}
          <p className="mt-2 text-sm text-muted-foreground">{q.rationale}</p>
        </li>
      ))}
    </ol>
  </section>
);

const Result = ({ a }) => {
  const r = a.result;
  const isDiag = a.mode === "diagnostic";
  const passed = r.passed;
  return (
    <PageShell>
      <div data-testid="quiz-result" className="mx-auto max-w-2xl">
        <div className="overline-label">{getLang() === "fr" ? `Résultat · ${t(MODE_LABEL[a.mode])}` : `${MODE_LABEL[a.mode]} result`} {a.module_id !== "DIAG" && `· ${a.module_id}`}</div>
        <div data-testid="quiz-result-score" className="mt-3 font-heading text-7xl font-black tracking-tighter">{r.score}%</div>
        <p data-testid="quiz-result-summary" className="mt-2 text-muted-foreground">{resultSummary(r, isDiag)}</p>
        {isDiag && <p data-testid="quiz-result-placement" className="mt-6 text-lg">{t("Placement")}{colon()} <strong>{TIER_COPY[r.placement_level].label}</strong>. {t("placement-note") === "placement-note" ? "Your path still starts at Foundation; the 80% standard never changes." : t("placement-note")}</p>}
        {!isDiag && passed && <p data-testid="quiz-result-passed" className="mt-6 text-lg text-brand-accent">{t("You met the 80% standard")}{a.mode === "certified" ? t(" — the next module is unlocked.") : "."}</p>}
        {!isDiag && !passed && (
          <p data-testid="quiz-result-below-standard" className="mt-6 text-lg">{tx("below-standard", "You are below the 80% certification standard for this module. Review the highlighted lessons, then take a new attempt with a different question set.")}</p>
        )}
        {r.tier_status === "repeat_course" && <p data-testid="quiz-result-repeat-course" className="mt-4 border-l-4 border-brand-action bg-card p-4 text-sm">{tx("repeat-course", "Status: Repeat course. Your tier progress has been reset — re-read all lessons from Module 1, then take fresh certified attempts.")}</p>}
        {r.tier_status === "capstone_pending" && <p className="mt-4 border-l-4 border-brand-primary bg-card p-4 text-sm">{tx("capstone-open", "All eight Strategist modules passed. The capstone is now open.")}</p>}
        {r.preview_only && <p data-testid="quiz-result-preview-only" className="mt-4 border-l-4 border-brand-primary bg-card p-4 text-sm">{tx("admin-preview", "Admin preview: level completed, but no certificate is issued for admin accounts.")}</p>}
        {r.certificate_id && <Link to={`/certificates/${r.certificate_id}`} data-testid="quiz-result-certificate-link" className="mt-6 inline-block rounded-sm bg-brand-action px-5 py-2.5 font-semibold text-brand-action-fg">{tx("View your certificate", `View your ${TIER_COPY[a.tier].label} certificate`)} →</Link>}
        {r.remediation?.length > 0 && (
          <div className="mt-8">
            <div className="overline-label">{t("Remediation areas")}</div>
            <ul data-testid="quiz-result-remediation" className="mt-3 space-y-2 text-sm">{r.remediation.map((tag) => <li key={tag} className="border-b border-border pb-2">{r.remediation_links?.[tag] ? <Link to={`/lessons/${r.remediation_links[tag]}`} className="hover:text-brand-primary hover:underline">{tag} →</Link> : tag}</li>)}</ul>
          </div>
        )}
        <div className="mt-10 flex gap-3">
          <Link to={a.lesson_id ? `/lessons/${a.lesson_id}` : a.module_id === "DIAG" ? "/dashboard" : `/modules/${a.module_id}`} data-testid="quiz-result-back-button" className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:bg-muted">{t(a.lesson_id ? "Back to lesson" : a.module_id === "DIAG" ? "Back to my path" : "Back to module")}</Link>
        </div>
        {a.review?.length > 0 && <Review items={a.review} />}
      </div>
    </PageShell>
  );
};

export default function Quiz() {
  const { attemptId } = useParams();
  const qc = useQueryClient();
  const [a, setA] = useState(null);
  const [selected, setSelected] = useState(null);
  const [fb, setFb] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(null);

  useEffect(() => {
    api.get(`/quiz/attempts/${attemptId}`).then(({ data }) => setA(data)).catch((e) => toast.error(formatApiError(e)));
  }, [attemptId]);

  if (!a) return <Loader />;
  if (a.result && !fb) return <Result a={a} />;
  if (!a.question) return <Loader />;

  const submit = async (reveal) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quiz/attempts/${attemptId}/answer`, { selected });
      if (data.done) qc.invalidateQueries({ queryKey: ["progress"] });
      if (reveal) {
        setFb(data.feedback);
        setPending(data.attempt);
      } else {
        setA(data.done ? (await api.get(`/quiz/attempts/${attemptId}`)).data : data.attempt);
        setSelected(null);
        setHint(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const next = async () => {
    setA(pending?.result ? (await api.get(`/quiz/attempts/${attemptId}`)).data : pending);
    setFb(null);
    setPending(null);
    setSelected(null);
    setHint(null);
  };
  const q = a.question;
  const lastQ = a.index + 1 === a.total;
  return (
    <PageShell>
      <div className="mx-auto max-w-3xl">
        <MachineTranslationBanner />
        <div className="flex items-center justify-between text-sm">
          <span data-testid="quiz-counter" className="font-mono">{t("Question")} {a.index + 1} {t("of")} {a.total}</span>
          <span data-testid="quiz-mode-label" className="overline-label">{t(MODE_LABEL[a.mode])} {a.module_id !== "DIAG" ? `· ${a.module_id}` : `· ${t("placement only")}`}</span>
        </div>
        <Progress value={(a.index / a.total) * 100} className="mt-3 h-2 rounded-full" />
        <div data-testid="quiz-difficulty" className="mt-2 text-xs capitalize text-muted-foreground">{t(q.difficulty)}</div>
        <h1 data-testid="quiz-stem" className="fade-up mt-8 font-heading text-2xl font-bold leading-snug sm:text-3xl">{q.stem}</h1>
        <div role="radiogroup" className="mt-8 space-y-3">
          {q.options.map((o) => <Option key={o.key} o={o} selected={selected === o.key} locked={!!fb} onSelect={setSelected} />)}
        </div>
        {fb && <Feedback fb={fb} mode={a.mode} />}
        {!fb && <HintPanel attemptId={attemptId} mode={a.mode} hint={hint} setHint={setHint} locked={!!fb} />}
        <div className="mt-8 flex flex-wrap items-center justify-end gap-3">
          {fb ? (
            <button data-testid="quiz-next-button" onClick={next} className="rounded-full bg-brand-primary px-7 py-3 font-semibold text-brand-primary-fg hover:opacity-90">{pending?.result ? t("See result") : t("Next question →")}</button>
          ) : (
            <>
              {a.mode === "practice" && <button data-testid="quiz-show-answer-button" onClick={() => submit(true)} disabled={!selected || busy} className="rounded-full border-2 border-brand-primary px-6 py-3 font-semibold text-brand-primary hover:bg-brand-primary/10 disabled:opacity-45">{t("Show answer")}</button>}
              <button data-testid="quiz-submit-answer-button" onClick={() => submit(false)} disabled={!selected || busy} className="rounded-full bg-brand-primary px-7 py-3 font-semibold text-brand-primary-fg hover:opacity-90 disabled:opacity-45">{lastQ ? t("Finish quiz") : `${t("Next")} →`}</button>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
}
